import { memo } from 'react';
import { T, useT } from '../../../translation/src';
import { PropertiesSection, Property } from './PropertiesSection';
import { PropertyPanel } from '../../Properties/components/PropertyPanel';
import { AnnotationType, type PropertyInstance } from '../../Properties/types';
import { getSketchObjectAnchorProperty, getSketchObjectTypeIdProperty, SKETCH_OBJECT_DIMENSION_PROPERTY_IDS } from '@pointorama/potree-shared';
import { usePropertyValue } from '../../Properties/reactive/usePropertyValue';
import { getSketchObjectType } from '../../../potree/sketchObjects';
import { ConvertType, useFormatNumber } from '../../../hooks/useFormatNumber';

interface SketchObjectPropertiesProps {
  projectId: string;
  identifier: string;
}

/** The length-label rows: dimensions only. */
const DIMENSION_PROPERTIES = new Set(SKETCH_OBJECT_DIMENSION_PROPERTY_IDS);
/** What a dimension leaves out: its size is what it measures (endpoints only). */
const DIMENSION_HIDDEN_PROPERTIES = new Set(['sketchobject_width', 'sketchobject_depth']);

const objectInstanceFilter = (instance: PropertyInstance): boolean => !DIMENSION_PROPERTIES.has(instance.config.identifier);
const dimensionInstanceFilter = (instance: PropertyInstance): boolean => !DIMENSION_HIDDEN_PROPERTIES.has(instance.config.identifier);

/** The measured length of a dimension: the distance between its two endpoints, in the user's unit. */
const DimensionLength: React.FC2<{ identifier: string }> = ({ identifier }) => {
  const { formatNumber } = useFormatNumber();
  const anchor = usePropertyValue(getSketchObjectAnchorProperty(identifier));
  const length = anchor.length >= 2 ? Math.hypot(anchor[1].x - anchor[0].x, anchor[1].y - anchor[0].y) : 0;
  return <Property title={<T _str="length" swc />}>{formatNumber(length, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>;
};

/**
 * Right-panel properties for a SKETCH_OBJECT (furniture/fixture symbol). The
 * shared props (width, depth, distance to wall) plus the selected object's
 * OWN per-type params (registered as `sketchobject_<typeId>_<param>` with a
 * visibility condition on `sketchobject_type_id`) all render through the
 * auto-generated {@link PropertyPanel}. A small read-out surfaces the type.
 * A dimension shows its measured length (read-only: its endpoints set it)
 * and its label decimals instead of a size.
 */
const SketchObjectProperties_: React.FC2<SketchObjectPropertiesProps> = ({ projectId, identifier }) => {
  const typeId = usePropertyValue(getSketchObjectTypeIdProperty(identifier));
  const type = typeId ? getSketchObjectType(typeId) : null;
  // Type labels are registered with the extraction-only t() marker — the
  // display layer translates by source string.
  const typeLabel = useT(type?.label ?? typeId ?? '', { swc: true });
  const dimension = type?.dimension ?? false;

  return (
    <div className="flex flex-col space-y-4 divide-y divide-gray-800">
      {/* Empty leading child so `divide-y` draws a line ABOVE the first section,
          matching the divider it renders between the other sections. */}
      <div />
      <PropertiesSection title={dimension ? <T _str="dimension" swc /> : <T _str="object" swc />}>
        <Property title={<T _str="type" swc />}>{typeLabel}</Property>
        {dimension && <DimensionLength identifier={identifier} />}
      </PropertiesSection>
      <PropertyPanel
        projectId={projectId}
        annotationTypes={[AnnotationType.SKETCH_OBJECT]}
        objectIdentifiers={[identifier]}
        instanceFilter={dimension ? dimensionInstanceFilter : objectInstanceFilter}
      />
    </div>
  );
};

export const SketchObjectProperties = memo(SketchObjectProperties_);
